/**
 * MonthEndReconciliationBanner — nudges the tenant on the 1st working day of
 * each month to upload each active vendor's ledger for the previous month.
 *
 * Behavior:
 *  • Fetches /api/reconciliation/nudge once on Dashboard mount.
 *  • Shows only when the backend says `show: true` (in the first-working-day
 *    window AND at least one active vendor is missing a fresh ledger).
 *  • Dismissible per-month via localStorage `billlens.nudge.dismissed.<YYYY-MM>`
 *    so the same nudge doesn't nag the user every day.
 *  • Lists pending vendors with a direct "Upload ledger" link to their
 *    Vendor detail page (which houses the reconciliation panel).
 *
 * Non-goals for MVP:
 *  • No email/WhatsApp reminder — in-app banner only.
 *  • National holiday calendar not modelled — we only skip Sat/Sun.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X, CalendarCheck, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../lib/api';

function dismissKey(periodTo) {
    // Period label like "2026-06-30" → dismiss key "2026-06"
    const ym = (periodTo || '').slice(0, 7) || 'unknown';
    return `billlens.nudge.dismissed.${ym}`;
}

export function MonthEndReconciliationBanner() {
    const [data, setData] = useState(null);
    const [dismissed, setDismissed] = useState(false);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        api.getReconciliationNudge()
            .then((d) => { if (!cancelled) setData(d); })
            .catch(() => { if (!cancelled) setData(null); });
        return () => { cancelled = true; };
    }, []);

    // Recompute dismissal from localStorage when data lands.
    useEffect(() => {
        if (!data) return;
        const key = dismissKey(data?.period?.to);
        try {
            setDismissed(localStorage.getItem(key) === '1');
        } catch {
            setDismissed(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.period?.to]);

    const pending = useMemo(
        () => (data?.vendors || []).filter((v) => v.needs_upload),
        [data]
    );

    if (!data || !data.show || dismissed || pending.length === 0) return null;

    const dismiss = () => {
        try { localStorage.setItem(dismissKey(data.period?.to), '1'); } catch { /* noop */ }
        setDismissed(true);
    };

    return (
        <Card
            className="p-4 md:p-5 border-[hsl(var(--info-border))] bg-[hsl(var(--info-bg))] text-[hsl(var(--info-fg))]"
            data-testid="month-end-nudge-banner"
        >
            <div className="flex items-start gap-3">
                <div className="mt-0.5">
                    <CalendarCheck size={20} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-heading text-base leading-tight">
                        Month-end check-in — reconcile {data.period?.label || 'last month'}
                    </div>
                    <div className="text-sm mt-0.5 opacity-90">
                        {pending.length === 1
                            ? `Upload 1 vendor's ledger to reconcile last month's books.`
                            : `Upload ${pending.length} vendors' ledgers to reconcile last month's books.`}
                        {' '}
                        Ease My Bill will diff their invoices/payments against your scans and payments.
                    </div>

                    {expanded && (
                        <ul
                            className="mt-3 space-y-1.5"
                            data-testid="month-end-nudge-vendor-list"
                        >
                            {pending.slice(0, 10).map((v) => (
                                <li
                                    key={v.vendor_key}
                                    className="flex items-center justify-between gap-2 rounded-md bg-background/60 border border-border/40 px-2.5 py-1.5"
                                >
                                    <div className="min-w-0">
                                        <div className="text-sm truncate">{v.vendor || v.vendor_key}</div>
                                        <div className="text-[11px] text-muted-foreground">
                                            {v.bills_count} bill{v.bills_count === 1 ? '' : 's'} · ₹{Number(v.total_billed || 0).toLocaleString('en-IN')}
                                        </div>
                                    </div>
                                    <Link to={`/vendors/${encodeURIComponent(v.vendor_key)}`}>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="gap-1 h-8"
                                            data-testid={`month-end-nudge-upload-${v.vendor_key}`}
                                        >
                                            Upload <ArrowRight size={12} />
                                        </Button>
                                    </Link>
                                </li>
                            ))}
                            {pending.length > 10 && (
                                <li className="text-xs text-muted-foreground pl-1">
                                    …and {pending.length - 10} more.
                                </li>
                            )}
                        </ul>
                    )}

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Button
                            size="sm"
                            onClick={() => setExpanded((v) => !v)}
                            variant="secondary"
                            className="gap-1.5"
                            data-testid="month-end-nudge-toggle"
                        >
                            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            {expanded ? 'Hide vendors' : `Show ${pending.length} vendor${pending.length === 1 ? '' : 's'}`}
                        </Button>
                        <Link to="/vendors">
                            <Button size="sm" variant="outline" className="gap-1">
                                Go to Vendors <ArrowRight size={14} />
                            </Button>
                        </Link>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={dismiss}
                    aria-label="Dismiss month-end reconciliation nudge"
                    data-testid="month-end-nudge-dismiss"
                    className="text-muted-foreground hover:text-foreground p-1 -m-1"
                >
                    <X size={16} />
                </button>
            </div>
        </Card>
    );
}
